import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import intl from "react-intl-universal";

import antennaUtils from "../../../../../script/utils/antennaUtils";
import operatorUtils from "../../../../../script/utils/operatorUtils";

import { useInput } from "../../../../hooks/useInput";
import { useOnChange } from "../../../../hooks/useOnChange";
import { EntryContext } from "../../../../contexts/EntryContext";

import { Grid } from "../../../../components/Grid";
import { Spacer } from "../../../../components/Spacer";
import { InputText } from "../../../../components/InputText";
import { SelectSingle } from "../../../../components/SelectSingle";

const LayerStyle = styled.div`
    display: ${props => (props.$show ? "flex" : "none")};
`;

const AntennaRowStyle = styled.div`
    display: flex;
    cursor: pointer;
    &:hover {
        background: ${props => props.theme.greyLightest};
    }
    >div:nth-child(1){
        width: 12px;
        text-align: left;
        margin-right: 8px
    }
    >div:nth-child(2){
        flex-grow: 1;
        width: 128px;
        margin-right: 8px;
        word-break: break-all;
        overflow-wrap: break-word;
    }
    >div:nth-child(3){
        width: 64px;
        margin-right: 8px
    }
    >div:nth-child(4){
        width: 28px;
        text-align: right;
        margin-right: 8px
    }
    >div:nth-child(5){
        width: 28px;
        text-align: right;
        margin-right: -14px
    }
`;

export const AntennasLayer = props => {
    const { plot, show } = props;

    const layerName = "antennasLayer";

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;

    const [antennas, setAntennas] = useState([]);
    const [selectedArrow, setSelectedArrow] = useState(null);

    const antenna = useInput("antenna", null);
    const selectedX = useInput("selectedX", null);
    const selectedY = useInput("selectedY", null);

    useEffect(() => {
        const layer = plot.addLayer(layerName, 180);
        plot.applyArrowFeature(layer);
    }, []);

    useEffect(() => {
        if (show) {
            plot.getLayer(layerName).activate();
        }
    }, [show]);

    useEffect(() => {
        const layer = plot.getLayer(layerName);
        layer.removeObjects();
        const antennas = antennaUtils.loadAntennas(entry);
        drawAntennas(antennas);
        setAntennas(antennas);
    }, [JSON.stringify(entry.sectors)]);

    useEffect(() => {
        const layer = plot.getLayer(layerName);
        const { feature } = layer;
        feature.onSelect(arrow => selectAntenna(arrow));
        feature.onModify(arrow => applyPosition(arrow));
        feature.onPlace(selectedArrow, () => applyPosition(selectedArrow));
    }, [antenna.value, selectedArrow, antennas]);

    useOnChange((name, value) => {
        selectedX.set(value.positionX);
        selectedY.set(value.positionY);
        const layer = plot.getLayer(layerName);
        const found = layer.objects.find(arrow => arrow.key === value.index);
        setSelectedArrow(found);
        plot.canvas.setActiveObject(found);
        plot.canvas.renderAll();
    }, [antenna]);

    const selectAntenna = arrow => {
        setSelectedArrow(arrow);
        if (!arrow) {
            return;
        }
        const found = antennas.find(antenna => antenna.index === arrow.key);
        antenna.set(found || null);
    };

    const applyPosition = arrow => {
        if (antenna.value !== null) {
            const entry = entryContext.getEntry();
            const { x, y } = arrow.getPosition();
            const positionX = Number((x * plot.refScale).toFixed(2));
            const positionY = Number((y * plot.refScale * -1).toFixed(2));
            selectedX.set(positionX);
            selectedY.set(positionY);
            const sector = entry.sectors.find(s => s.key === antenna.value.key);
            if (sector) {
                sector.positionX = positionX;
                sector.positionY = positionY;
            }
            entryContext.setEntry(entry);
        }
    };

    const drawAntennas = antennas => {
        const layer = plot.getLayer(layerName);
        for (const antenna of antennas.values()) {
            const positionX = antenna.positionX ?? 0;
            const positionY = antenna.positionY ?? 0;
            const pixelX = positionX / plot.refScale;
            const pixelY = positionY / plot.refScale * -1;
            const imageProperties = { color: operatorUtils.getColor(antenna.operator) };
            plot.addArrow(layer, antenna.index, pixelX, pixelY, antenna.direction, true, imageProperties);
        }
    };

    const getSelectLabel = value => {
        if (value) {
            const an = value.antennaName;
            const antennaName = an?.length > 24 ? an.substring(0, 24) + "..." : an;
            const { index, operatorShortcut, direction } = value;
            const height = value.height?.toFixed(2);
            const values = [index + 1, antennaName, operatorShortcut, direction, height];
            if (values && values.length > 0) {
                return values.filter(entry => (entry || entry === 0) && entry !== "").join(" | ");
            }
        }
        return "";
    };

    return (
        <LayerStyle $show={show}>
            <SelectSingle {...antenna.bind}
                title={intl.get("entry.plan.antennas.title")}
                toLabel={value => getSelectLabel(value)}>
                {antennas.map(value =>
                    <option key={getSelectLabel(value)} value={value}>
                        <AntennaRowStyle>
                            <div>{value.index + 1}</div>
                            <div>{value.antennaName}</div>
                            <div>{value.operator ? intl.get(`enum.operator.${value.operator}`) : ""}</div>
                            <div>{value.direction}</div>
                            <div>{value.height?.toFixed(2)}</div>
                        </AntennaRowStyle>
                    </option>
                )}
            </SelectSingle>
            <Spacer variant="horizontal" />
            <Grid columns="2">
                <InputText {...selectedX.bind} title={intl.get("entry.plan.antennas.selectedX")} disabled />
                <InputText {...selectedY.bind} title={intl.get("entry.plan.antennas.selectedY")} disabled />
            </Grid>
        </LayerStyle>
    );
};